import React, { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import EmptyComponentsSvg from 'images/v5/empty-states/components.png';
import PlusSvg from 'images/v5/icons/plus.svg';
import Button from 'layout/atoms/Button/Button';
import InfiniteScroll from 'layout/atoms/InfiniteScroll';
import Loader from 'layout/atoms/Loader/Loader';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import ComponentCard from 'molecules/ComponentCard/ComponentCard';
import Grid from 'molecules/Grid/Grid';
import { parseProductCard } from 'parsers/specification/parseProduct';
import { fetchComponents, resetComponents, resetFilters, selectComponentsErrors, selectComponentsStatus, setFilter, } from 'store/componentsSlice';
import { openModal } from 'store/layoutSlice';
function ComponentsList({ scrollableTarget = 'mainContent', category, selectable, }) {
    const dispatch = useDispatch();
    const routeParams = useParams();
    const params = category || routeParams;
    const { t, i18n } = useTranslation(['specification', 'exceptions', 'emptyStates']);
    const components = useSelector((state) => state.components.items);
    const categories = useSelector((state) => state.components.categories.items);
    const filters = useSelector((state) => state.components.filters);
    const pageInfo = useSelector((state) => state.components.pageInfo);
    const status = useSelector(selectComponentsStatus).fetch;
    const error = useSelector(selectComponentsErrors).fetch;
    const categoriesReady = useSelector((state) => { var _a, _b; return (_b = (_a = state.components) === null || _a === void 0 ? void 0 : _a.categories) === null || _b === void 0 ? void 0 : _b.status; }) === 'succeeded';
    const clearAndFetchComponents = useCallback(() => {
        dispatch(resetComponents());
        dispatch(fetchComponents());
    }, [dispatch]);
    const fetchMoreComponents = () => {
        if (pageInfo.hasNextPage && status !== 'loading') {
            dispatch(fetchComponents());
        }
    };
    useEffect(() => {
        if (params.categorySlug && categories) {
            const category = categories.find((category) => category.slug === params.categorySlug);
            const categoryId = (category === null || category === void 0 ? void 0 : category.id) || null;
            dispatch(setFilter({ filterName: 'categoryId', value: categoryId }));
        }
        if (!params.categorySlug && categories) {
            dispatch(setFilter({ filterName: 'categoryId', value: null }));
        }
        if (filters && categoriesReady) {
            clearAndFetchComponents();
        }
    }, [
        dispatch,
        categoriesReady,
        categories,
        params.categorySlug,
        filters.name,
        filters.statusId,
        filters.supplierId,
        clearAndFetchComponents,
        filters,
    ]);
    useEffect(() => {
        return () => {
            dispatch(resetFilters());
        };
    }, [dispatch]);
    if (components.length === 0 && (status === 'loading' || status === 'idle')) {
        return React.createElement(Loader, null);
    }
    if (components.length === 0 && status === 'succeeded') {
        const hasActiveFilters = Boolean(filters.name) ||
            Boolean(filters.statusId) ||
            Boolean(filters.supplierId) ||
            Boolean(filters.categoryId);
        if (hasActiveFilters) {
            return (React.createElement(EmptyState, { header: t('emptyStates:no_items_header'), subheader: t('emptyStates:no_components_found_for_filter') }));
        }
        return (React.createElement(EmptyState, { icon: React.createElement("img", { src: EmptyComponentsSvg, alt: "" }), header: t('emptyStates:COMPONENTS_HEADER'), subheader: t('emptyStates:COMPONENTS_SUBHEADER') },
            React.createElement(Button, { leftIcon: React.createElement(PlusSvg, null), onClick: () => dispatch(openModal({ name: 'componentCreator' })) }, t('emptyStates:COMPONENTS_ADD_BUTTON'))));
    }
    if (status === 'failed') {
        return React.createElement("div", null, error);
    }
    return (React.createElement(InfiniteScroll, { dataLength: components.length, next: fetchMoreComponents, hasMore: pageInfo.hasNextPage, loader: React.createElement(Loader, null), scrollableTarget: scrollableTarget },
        React.createElement(Grid, { columns: "flexible", gap: 12 }, components.map((component) => {
            return (React.createElement(ComponentCard, { key: component === null || component === void 0 ? void 0 : component.id, product: parseProductCard(component, i18n), onlyActiveVariants: true, selectable: selectable }));
        }))));
}
export default memo(ComponentsList);
